import type {JSX} from 'react';

import {cn} from '@/utils/cn';

interface TableProps {
  children?: React.ReactNode;
  className?: string;
  containerDataCy?: string;
  // Open-ended so callers can pass through any DOM attribute of the element
  // this wrapper renders; `unknown` still forwards cleanly through the spread.
  [rest: string]: unknown;
}

// Full Table
export function Table({
  children,
  className = '',
  containerDataCy,
  ...rest
}: TableProps): JSX.Element {
  return (
    <div
      data-cy={containerDataCy}
      className="w-full overflow-x-auto rounded-xl rounded-bl-none rounded-br-none border dark:border-dark-borderMid"
    >
      {/* cn (not string concatenation) throughout this file, so a caller's
          className can actually override a base class. With `important: true`
          in tailwind.config both would compile !important and the winner would
          come down to generated-stylesheet order rather than the caller's
          intent; cn drops the overridden class from the attribute entirely.
          Notably this lets a table opt out of `w-full` (e.g. with `w-max`) so
          it sizes to its content and scrolls inside the wrapper above instead
          of being compressed to fit it.

          Caveat: twMerge only knows the DEFAULT Tailwind scale, so this
          project's custom widths (w-inherit, w-450, ...) are unregistered and
          will NOT displace a base `w-*`. Register them via extendTailwindMerge
          if a caller ever needs one of those to win. */}
      <table
        className={cn(
          'min-w-[640px] w-full divide-y divide-gray-300 dark:divide-gray-800 shadow-sm',
          className,
        )}
        {...rest}
      >
        {children}
      </table>
    </div>
  );
}

// Table Head
export function Thead({
  children,
  className = '',
  ...rest
}: TableProps): JSX.Element {
  return (
    <thead
      className={cn(
        'bg-gray-50 dark:bg-dark-zebraRow dark:text-[#E4E6E9] rounded-xl',
        className,
      )}
      {...rest}
    >
      {children}
    </thead>
  );
}

// Table Body
export function TBody({
  children,
  className = '',
  ...rest
}: TableProps): JSX.Element {
  return (
    <tbody
      className={cn(
        'bg-white dark:bg-transparent divide-y divide-gray-200 dark:divide-gray-800 dark:border-dark-borderMid',
        className,
      )}
      {...rest}
    >
      {children}
    </tbody>
  );
}

interface TrProps extends TableProps {
  enableHover?: boolean;
  colspan?: number;
}

// Table Row
export function Tr({
  children,
  className = '',
  enableHover = true,
  ...rest
}: TrProps): JSX.Element {
  return (
    <tr
      className={cn(
        enableHover && 'hover:bg-gray-100 dark:hover:bg-gray-900',
        'transition-colors duration-500',
        className,
      )}
      {...rest}
    >
      {children}
    </tr>
  );
}

interface TdProps extends TableProps {
  isNumeric?: boolean;
  colspan?: number;
}

// Table Cell
export function Td({
  children,
  colspan = 1,
  className = '',
  isNumeric = false,
  ...rest
}: TdProps): JSX.Element {
  return (
    <td
      colSpan={colspan}
      className={cn(
        'py-4 whitespace-nowrap text-sm text-gray-800 dark:text-[#E8E8E8]',
        isNumeric ? 'text-center' : 'text-left',
        className,
      )}
      {...rest}
    >
      {children}
    </td>
  );
}

interface ThProps extends TableProps {
  isNumeric?: boolean;
}

// Table Header Cell
export function Th({
  children,
  className = '',
  isNumeric = false,
  ...rest
}: ThProps): JSX.Element {
  return (
    <th
      scope="col"
      className={cn(
        'py-4 text-xs font-medium uppercase tracking-wider',
        isNumeric ? 'text-right' : 'text-left',
        className,
      )}
      {...rest}
    >
      {children}
    </th>
  );
}
